// components/ResetPassword/ResetPassword.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { validateResetToken, resetPassword } from '../../services/authService';
import './ResetPassword.scss';

const ResetPassword = () => {
  const location = useLocation();
  const navigate = useNavigate();
  
  const [token, setToken] = useState('');
  const [email, setEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // Valida o token ao carregar a página
  useEffect(() => {
    const validateToken = async () => {
      const queryParams = new URLSearchParams(location.search);
      const tokenParam = queryParams.get('token');
      const emailParam = queryParams.get('email');

      if (!tokenParam) {
        setError('Token inválido. Solicite uma nova redefinição.');
        setValidating(false);
        return;
      }

      setToken(tokenParam);
      setEmail(emailParam || '');

      try {
        const isValid = await validateResetToken(tokenParam);
        setTokenValid(isValid);
        
        if (!isValid) {
          setError('Este link é inválido ou expirou. Solicite uma nova redefinição.');
        }
      } catch (err) {
        setError('Erro ao validar token. Tente novamente.');
      } finally {
        setValidating(false);
      }
    };

    validateToken();
  }, [location]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Validações
    if (newPassword.length < 6) {
      setError('A senha deve ter pelo menos 6 caracteres.');
      setLoading(false);
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('As senhas não coincidem.');
      setLoading(false);
      return;
    }

    try {
      const response = await resetPassword(
        token,
        newPassword,
        confirmPassword
      );

      if (response.success) {
        setSubmitted(true);
        setSuccessMessage(response.message || 'Senha redefinida com sucesso!');
        
        // Redireciona para login após 3 segundos
        setTimeout(() => {
          navigate('/');
        }, 3000);
      } else {
        setError(response.message || 'Erro ao redefinir senha.');
      }
    } catch (err) {
      setError('Erro ao conectar ao servidor. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  if (validating) {
    return (
      <div className="reset-password">
        <div className="reset-password__container">
          <div className="reset-password__loading">
            <div className="reset-password__spinner" />
            <p>Validando link de redefinição...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="reset-password">
      <div className="reset-password__container">
        <h1 className="reset-password__title">Redefinir Senha</h1>

        {!tokenValid ? (
          <div className="reset-password__error-box">
            <div className="reset-password__error-icon">⛔</div>
            <h2>Link Inválido</h2>
            <p>{error}</p>
            <a href="/forgot-password" className="reset-password__link">
              Solicitar nova redefinição
            </a>
          </div>
        ) : submitted ? (
          <div className="reset-password__success">
            <div className="reset-password__success-icon">✓</div>
            <h2>Senha redefinida!</h2>
            <p>{successMessage}</p>
            <p className="reset-password__redirect">
              Redirecionando para o login...
            </p>
            <a href="/" className="reset-password__link">
              Ir para o login
            </a>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="reset-password__form">
            {email && (
              <div className="reset-password__info">
                <span>📧</span>
                <span>Redefinindo senha para: <strong>{email}</strong></span>
              </div>
            )}

            <div className="reset-password__field">
              <label htmlFor="newPassword">Nova Senha</label>
              <div className="reset-password__password-wrapper">
                <input
                  id="newPassword"
                  type={showPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Digite sua nova senha"
                  disabled={loading}
                  autoFocus
                />
                <button
                  type="button"
                  className="reset-password__toggle-password"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? '👁️' : '👁️‍🗨️'}
                </button>
              </div>
              <small>A senha deve ter pelo menos 6 caracteres.</small>
            </div>

            <div className="reset-password__field">
              <label htmlFor="confirmPassword">Confirmar Senha</label>
              <input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirme sua nova senha"
                disabled={loading}
              />
            </div>

            {error && (
              <div className="reset-password__error">
                <span>⚠️</span> {error}
              </div>
            )}

            <button 
              type="submit" 
              className="reset-password__button"
              disabled={loading}
            >
              {loading ? 'Redefinindo...' : 'Redefinir Senha'}
            </button>

            <a href="/" className="reset-password__link">
              ← Voltar ao login
            </a>
          </form>
        )}
      </div>
    </div>
  );
};

export default ResetPassword;